import type { EntrySuggestionDto } from '@tk/shared';
import { Bot, Check, CornerDownRight, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useKaannos } from '../lib/i18n';
import { useConfirmSuggestion, useDismissSuggestion } from '../lib/queries';
import { kentta, nappiEnsisijainen, nappiToissijainen } from '../lib/tyylit';
import AsiakasKoodi from './AsiakasKoodi';
import Dialogi from './Dialogi';
import KestoValitsin from './KestoValitsin';

interface Props {
  ehdotus: EntrySuggestionDto;
  auki: boolean;
  onSulje: () => void;
  onVahvistettu: (viesti: string) => void;
}

/**
 * Koneen ehdotuksen vahvistaminen kirjaukseksi (backlog 5).
 *
 * **Kesto alkaa tyhjänä eikä esitäytettynä.** Agentti kertoo kenelle ja
 * mitä; kauanko on ihmisen sanottava, koska näppäimistöaika ei ole
 * laskutettavaa aikaa. Esitäytetty luku olisi ankkuri, jonka ohi harva
 * korjaa — ja ankkuroitu arvio kantaisi saman vinouman raportteihin, jota
 * koko työnjako on rakennettu välttämään. Siksi tallennus on kiinni,
 * kunnes kestoon on koskettu.
 *
 * Perustelu näytetään aina. Se on ainoa asia, jonka nojalla ihminen voi
 * arvioida, onko ehdotus oikea — ja kuun lopussa ainoa jälki siitä, mistä
 * ehdotettu tunti tuli.
 */
export default function EhdotusVahvistus({ ehdotus, auki, onSulje, onVahvistettu }: Props) {
  const { t, m } = useKaannos();
  const vahvista = useConfirmSuggestion();
  const hylkaa = useDismissSuggestion();

  const [minuutit, setMinuutit] = useState<number | null>(null);
  const [kuvaus, setKuvaus] = useState(ehdotus.description ?? '');

  const virhe = vahvista.error ?? hylkaa.error;

  function laheta(tapahtuma: React.FormEvent) {
    tapahtuma.preventDefault();
    if (minuutit === null) return;

    vahvista.mutate(
      {
        id: ehdotus.id,
        input: { minutes: minuutit, description: kuvaus.trim() || null },
      },
      {
        onSuccess: (tulos) => {
          onVahvistettu(
            [t('ehdotus.vahvistettu'), ...tulos.warnings.map((varoitus) => varoitus.message)].join(
              ' ',
            ),
          );
          onSulje();
        },
      },
    );
  }

  return (
    <Dialogi
      otsikko={`${t('ehdotus.otsikko')} · ${m.pvm(ehdotus.entryDate)}`}
      auki={auki}
      onSulje={onSulje}
    >
      <form onSubmit={laheta} className="space-y-4">
        <p className="border-reuna bg-pinta text-teksti-vaimea flex items-start gap-2 rounded-md border border-dashed px-3 py-2 text-xs">
          <Bot className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{t('ehdotus.selite')}</span>
        </p>

        <dl className="space-y-3 text-sm">
          <Rivi nimike={t('yleinen.asiakas')}>
            <span className="flex items-center gap-1.5">
              <AsiakasKoodi code={ehdotus.customer.code} colorKey={ehdotus.customer.colorKey} />
              <span className="text-teksti-vaimea">{ehdotus.customer.name}</span>
            </span>
          </Rivi>

          <Rivi nimike={t('kirjaus.tehtavalaji')}>{ehdotus.taskType.name}</Rivi>

          {ehdotus.project && (
            <Rivi nimike={t('kirjaus.projekti')}>
              <span className="flex items-center gap-1">
                <CornerDownRight
                  className="text-teksti-vaimea size-3.5 shrink-0"
                  aria-hidden="true"
                />
                {ehdotus.project.name}
              </span>
            </Rivi>
          )}

          <Rivi nimike={t('ehdotus.perustelu')}>
            <span className="whitespace-pre-wrap">{ehdotus.rationale}</span>
          </Rivi>

          <Rivi nimike={t('ehdotus.lahde')}>
            <span className="text-teksti-vaimea">
              {ehdotus.source} · {m.pvmAika(ehdotus.createdAt)}
            </span>
          </Rivi>
        </dl>

        <div className="space-y-1">
          <span className="text-teksti-vaimea text-xs font-medium">{t('kirjaus.kesto')}</span>
          <KestoValitsin minutes={minuutit} onChange={(arvo) => setMinuutit(arvo)} />
          <p className="text-teksti-vaimea text-xs">
            {minuutit === null ? t('ehdotus.kestoPuuttuu') : t('ehdotus.kestoOhje')}
          </p>
        </div>

        <label className="block space-y-1">
          <span className="text-teksti-vaimea text-xs font-medium">
            {t('kirjaus.kuvaus')} <span className="font-normal">{t('yleinen.valinnainen')}</span>
          </span>
          <textarea
            value={kuvaus}
            rows={2}
            maxLength={500}
            onChange={(tapahtuma) => setKuvaus(tapahtuma.target.value)}
            className={kentta}
          />
        </label>

        {virhe && (
          <p
            role="alert"
            className="border-yli/40 bg-yli/10 text-teksti rounded-md border px-3 py-2 text-sm"
          >
            {virhe.message}
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() =>
              hylkaa.mutate(ehdotus.id, {
                onSuccess: () => {
                  onVahvistettu(t('ehdotus.hylatty'));
                  onSulje();
                },
              })
            }
            disabled={hylkaa.isPending || vahvista.isPending}
            className={`${nappiToissijainen} flex items-center gap-1.5`}
          >
            <Trash2 className="size-4" aria-hidden="true" />
            {t('ehdotus.hylkaa')}
          </button>
          <button
            type="submit"
            disabled={minuutit === null || vahvista.isPending}
            className={`${nappiEnsisijainen} flex items-center gap-1.5`}
          >
            <Check className="size-4" aria-hidden="true" />
            {vahvista.isPending ? t('yleinen.tallennetaan') : t('ehdotus.vahvista')}
          </button>
        </div>
      </form>
    </Dialogi>
  );
}

function Rivi({ nimike, children }: { nimike: string; children: ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="text-teksti-vaimea w-28 shrink-0">{nimike}</dt>
      <dd className="text-teksti min-w-0 flex-1 break-words">{children}</dd>
    </div>
  );
}
